/**
 * Screenshot and screen recording for the submission panel (#1170), the
 * capture cycle of the former wizard (#475) without its step UI.
 *
 * While anything is captured, `active` is true and the panel hides itself, so
 * it is never in the picture. A screenshot is two steps: the browser's picker
 * first, then a floating "Jetzt aufnehmen" bar — capturing right after the
 * picker would catch the picker's own fade-out. The bar hides before the
 * frame is grabbed. A recording stops on the bar, on the browser's own "Stop
 * sharing", or after `MAX_RECORDING_SECONDS`.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

export const MAX_RECORDING_SECONDS = 30

/** Time for the panel's hide transition before the browser picker opens. */
const HIDE_DELAY_MS = 180

/** Screenshot stream rate; with the cap below at least a few fresh frames arrive after the bar hid. */
const SCREENSHOT_FPS = 15
const FRAME_WAIT_CAP_MS = 600

export type CapturePhase =
  /** Nothing captured. */
  | 'idle'
  /** The browser's picker is open. */
  | 'picking'
  /** Screenshot: the stream runs, the bar waits for "Jetzt aufnehmen". */
  | 'ready'
  /** Screenshot: the bar is hidden, the frame is being taken. */
  | 'grabbing'
  | 'recording'
  /** Recording: the bar is hidden, the recorder flushes. */
  | 'stopping'

export type CaptureError = 'unsupported' | 'failed'

interface Options {
  onScreenshot: (png: Blob) => void
  onRecording: (webm: Blob) => void
  onError: (error: CaptureError) => void
}

const nextPaint = () => new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))
const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

function displayMediaAvailable(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function'
}

function recordingMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) return 'video/webm;codecs=vp9'
  return MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : ''
}

export function useScreenCapture({ onScreenshot, onRecording, onError }: Options) {
  const [phase, setPhase] = useState<CapturePhase>('idle')
  const [secondsLeft, setSecondsLeft] = useState(MAX_RECORDING_SECONDS)

  const streamRef = useRef<MediaStream | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const callbacks = useRef({ onScreenshot, onRecording, onError })
  callbacks.current = { onScreenshot, onRecording, onError }

  const release = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = null
    if (videoRef.current) videoRef.current.srcObject = null
    videoRef.current = null
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  useEffect(() => release, [release])

  const requestStream = async (video: MediaTrackConstraints): Promise<MediaStream | null> => {
    if (!displayMediaAvailable()) {
      callbacks.current.onError('unsupported')
      return null
    }
    setPhase('picking')
    await wait(HIDE_DELAY_MS)
    try {
      return await navigator.mediaDevices.getDisplayMedia({
        video,
        audio: false,
        selfBrowserSurface: 'include',
        // Chrome offers this tab first — what is reported is almost always here.
        preferCurrentTab: true,
      } as DisplayMediaStreamOptions)
    } catch {
      // The person closed the picker: nothing to report.
      setPhase('idle')
      return null
    }
  }

  const cancel = useCallback(() => {
    const recorder = recorderRef.current
    recorderRef.current = null
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null
      recorder.stop()
    }
    release()
    setPhase('idle')
  }, [release])

  const startScreenshot = async () => {
    const stream = await requestStream({ frameRate: SCREENSHOT_FPS, width: { ideal: 3840 }, height: { ideal: 2160 } })
    if (!stream) return
    streamRef.current = stream
    try {
      const video = document.createElement('video')
      video.muted = true
      video.srcObject = stream
      videoRef.current = video
      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve()
        video.onerror = () => reject(new Error('TICKET_CAPTURE_VIDEO_ERROR'))
        setTimeout(() => reject(new Error('TICKET_CAPTURE_TIMEOUT')), 6000)
      })
      await video.play()
      stream.getVideoTracks()[0]?.addEventListener('ended', cancel)
      setPhase('ready')
    } catch {
      release()
      setPhase('idle')
      callbacks.current.onError('failed')
    }
  }

  const takeScreenshot = async () => {
    const video = videoRef.current
    if (!video) return
    setPhase('grabbing')
    await nextPaint()
    // Take a frame from after the bar disappeared: the second new frame (the
    // first may have been captured before the repaint). A detached video may
    // never report frames (seen in headless Chrome), so the wait is capped at
    // several frame intervals instead.
    await new Promise<void>((resolve) => {
      const withFrameCallback = video as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => void }
      withFrameCallback.requestVideoFrameCallback?.(() => withFrameCallback.requestVideoFrameCallback?.(() => resolve()))
      setTimeout(resolve, FRAME_WAIT_CAP_MS)
    })
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d')?.drawImage(video, 0, 0)
    release()
    try {
      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('TICKET_CAPTURE_EXPORT_FAILED'))), 'image/png'),
      )
      setPhase('idle')
      callbacks.current.onScreenshot(blob)
    } catch {
      setPhase('idle')
      callbacks.current.onError('failed')
    }
  }

  const stopRecording = useCallback(async () => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state !== 'recording') return
    // The bar goes first, so the stop button is not in the last frames.
    setPhase('stopping')
    await nextPaint()
    if (recorder.state === 'recording') recorder.stop()
  }, [])

  // The time limit: the bar's countdown reaching zero stops like the button.
  useEffect(() => {
    if (phase === 'recording' && secondsLeft === 0) void stopRecording()
  }, [phase, secondsLeft, stopRecording])

  const startRecording = async () => {
    if (typeof MediaRecorder === 'undefined') {
      callbacks.current.onError('unsupported')
      return
    }
    const stream = await requestStream({ frameRate: 15, width: { ideal: 1920 }, height: { ideal: 1080 } })
    if (!stream) return
    streamRef.current = stream
    try {
      const mimeType = recordingMimeType()
      const recorder = new MediaRecorder(stream, { ...(mimeType ? { mimeType } : {}), videoBitsPerSecond: 2_500_000 })
      const chunks: Blob[] = []
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data)
      }
      recorder.onstop = () => {
        recorderRef.current = null
        release()
        setPhase('idle')
        callbacks.current.onRecording(new Blob(chunks, { type: 'video/webm' }))
      }
      recorderRef.current = recorder
      stream.getVideoTracks()[0]?.addEventListener('ended', () => void stopRecording())
      recorder.start(200)
      setSecondsLeft(MAX_RECORDING_SECONDS)
      setPhase('recording')
      timerRef.current = setInterval(() => setSecondsLeft((left) => Math.max(0, left - 1)), 1000)
    } catch {
      release()
      setPhase('idle')
      callbacks.current.onError('failed')
    }
  }

  return {
    phase,
    secondsLeft,
    /** The panel hides itself while this is true. */
    active: phase !== 'idle',
    startScreenshot,
    takeScreenshot,
    startRecording,
    stopRecording,
    cancel,
  }
}
